/**
 * Live Razorpay checkout — web + mobile. Payment recorded only after checkout + signature verify.
 */
import RazorpayService from '../core/razorpay';

const CHECKOUT_SRC = 'https://checkout.razorpay.com/v1/checkout.js';

function loadCheckoutScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.Razorpay) return resolve();
    const existing = document.querySelector(`script[src="${CHECKOUT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Could not load Razorpay Checkout.')), { once: true });
      return;
    }
    const s = document.createElement('script');
    s.src = CHECKOUT_SRC;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Could not load Razorpay Checkout. Check your connection.'));
    document.body.appendChild(s);
  });
}

function openCheckoutStrict({ order, invoice, customer, method, orgName }) {
  return new Promise(async (resolve, reject) => {
    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      fn(value);
    };

    try {
      await loadCheckoutScript();
    } catch (e) {
      finish(reject, e);
      return;
    }

    if (!order?.keyId || !order?.id) {
      finish(reject, new Error('Online payment is not configured. Use Paid offline or contact support.'));
      return;
    }

    const methodMap = { upi: 'upi', card: 'card', nb: 'netbanking', emi: 'emi' };
    const rzp = new window.Razorpay({
      key: order.keyId,
      order_id: order.id,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: orgName || 'Founders Bridge',
      description: `Invoice ${invoice.invoiceNo}`,
      prefill: {
        name: customer?.name || '',
        email: customer?.email || '',
        contact: customer?.phone || '',
        method: methodMap[method] || undefined,
      },
      theme: { color: '#1E40AF' },
      handler: (response) => {
        if (!response?.razorpay_payment_id || !response?.razorpay_order_id || !response?.razorpay_signature) {
          finish(reject, new Error('Payment was not completed'));
          return;
        }
        finish(resolve, response);
      },
      modal: {
        confirm_close: true,
        ondismiss: () => finish(reject, new Error('Payment cancelled')),
      },
    });

    rzp.on('payment.failed', (resp) => {
      finish(reject, new Error(resp?.error?.description || 'Payment failed'));
    });

    rzp.open();
  });
}

let _paying = false;

export async function runRazorpayPaymentLive({
  invoice, amount, customer, method, orgName,
}) {
  if (_paying) throw new Error('Payment already in progress');
  _paying = true;
  try {
    const order = await RazorpayService.createOrder(invoice, amount);
    if (!order?.id || !order?.keyId) {
      throw new Error('Online payment is not configured. Use Paid offline or contact support.');
    }

    const rzpResponse = await openCheckoutStrict({
      order,
      invoice,
      customer,
      method,
      orgName,
    });

    const verify = await RazorpayService.verify(rzpResponse);
    if (!verify?.success) throw new Error('Payment verification failed');

    return { rzpResponse, order };
  } finally {
    _paying = false;
  }
}
